import { ProjectEvent } from './project-objects';

// Represents a node in the AProjectEventL tree
class EventNode<EventType extends ProjectEvent> {
	constructor(
		public measureNumber: number, // The key for this node is the measure number
		public projectEvent: EventType,
		public left: EventNode<EventType> | null = null, // Pointer to the left child
		public right: EventNode<EventType> | null = null, // Pointer to the right child
		public height: number = 1 // Height of the node, used for balancing
	) { }
}

// Balanced AVL tree for storing and querying project events by measure number
export class BalancedEventBST<EventType extends ProjectEvent> {
	private root: EventNode<EventType> | null = null; // Root of the tree

	constructor() { }

	// Insert a new measureNumber-projectEvent pair into the tree
	insert(measureNumber: number, projectEvent: EventType): void {
		this.root = this.insertEventNode(this.root, measureNumber, projectEvent);
	}

	// Get the projectEvent associated with a measure number or the closest lower measure number
	getLatest(measureNumber: number): EventType | undefined {
		const node = this.findClosestLowerOrEqual(this.root, measureNumber);
		return node?.projectEvent;
	}

	// Helper to recursively insert a new node
	private insertEventNode(node: EventNode<EventType> | null, measureNumber: number, projectEvent: EventType): EventNode<EventType> {
		if (!node) return new EventNode<EventType>(measureNumber, projectEvent);

		const measureDifference = measureNumber - node.measureNumber;
		if (measureDifference < 0) {
			node.left = this.insertEventNode(node.left, measureNumber, projectEvent); // Insert into the left subtree
		} else if (measureDifference > 0) {
			node.right = this.insertEventNode(node.right, measureNumber, projectEvent); // Insert into the right subtree
		} else {
			node.projectEvent = projectEvent; // Update projectEvent if the measure number already exists
		}

		return this.balance(node); // Balance the tree after insertion
	}

	// Find the closest lower or equal node to the given measure number
	private findClosestLowerOrEqual(node: EventNode<EventType> | null, measureNumber: number): EventNode<EventType> | null {
		let closest: EventNode<EventType> | null = null;

		while (node) {
			const measureDifference = measureNumber - node.measureNumber;
			if (measureDifference === 0) {
				return node; // Exact match
			} else if (measureDifference > 0) {
				closest = node; // Potential closest, move to the right
				node = node.right;
			} else {
				node = node.left; // Move to the left for smaller measure numbers
			}
		}

		return closest;
	}

	// Balance the tree after an insertion or deletion
	private balance(node: EventNode<EventType>): EventNode<EventType> {
		node.height = 1 + Math.max(this.getHeight(node.left), this.getHeight(node.right)); // Update node height

		const balanceFactor = this.getBalanceFactor(node); // Calculate balance factor

		// Left-heavy tree
		if (balanceFactor > 1 && node.left) {
			if (this.getBalanceFactor(node.left) < 0) {
				node.left = this.rotateLeft(node.left); // Left-Right case
			}
			return this.rotateRight(node); // Left-Left case
		}

		// Right-heavy tree
		if (balanceFactor < -1 && node.right) {
			if (this.getBalanceFactor(node.right) > 0) {
				node.right = this.rotateRight(node.right); // Right-Left case
			}
			return this.rotateLeft(node); // Right-Right case
		}

		return node;
	}

	// Perform a left rotation
	private rotateLeft(node: EventNode<EventType>): EventNode<EventType> {
		const newRoot = node.right!;
		node.right = newRoot.left;
		newRoot.left = node;

		// Update heights
		node.height = 1 + Math.max(this.getHeight(node.left), this.getHeight(node.right));
		newRoot.height = 1 + Math.max(this.getHeight(newRoot.left), this.getHeight(newRoot.right));

		return newRoot;
	}

	// Perform a right rotation
	private rotateRight(node: EventNode<EventType>): EventNode<EventType> {
		const newRoot = node.left!;
		node.left = newRoot.right;
		newRoot.right = node;

		// Update heights
		node.height = 1 + Math.max(this.getHeight(node.left), this.getHeight(node.right));
		newRoot.height = 1 + Math.max(this.getHeight(newRoot.left), this.getHeight(newRoot.right));

		return newRoot;
	}

	// Get the height of a node
	private getHeight(node: EventNode<EventType> | null): number {
		return node ? node.height : 0;
	}

	// Get the balance factor of a node
	private getBalanceFactor(node: EventNode<EventType>): number {
		return this.getHeight(node.left) - this.getHeight(node.right);
	}

	// Helper method for in-order traversal
	private inOrderTraversalToJsonList(node: EventNode<EventType> | null, result: any[]): void {
		if (!node) return;

		// Recur on the left subtree
		this.inOrderTraversalToJsonList(node.left, result);

		// Process the current node
		result.push(node.projectEvent.toJson(node.measureNumber));

		// Recur on the right subtree
		this.inOrderTraversalToJsonList(node.right, result);
	}

	private inOrderTraversalCopy(node: EventNode<EventType> | null, copy: BalancedEventBST<EventType>): void {
		if (!node) return;

		// Recur on the left subtree
		this.inOrderTraversalCopy(node.left, copy);

		// Process the current node
		copy.insert(node.measureNumber, node.projectEvent.createCopy() as EventType);

		// Recur on the right subtree
		this.inOrderTraversalCopy(node.right, copy);
	}

	public createCopy(): BalancedEventBST<EventType> {
		const copy = new BalancedEventBST<EventType>();
		this.inOrderTraversalCopy(this.root, copy);
		return copy;
	}

	public toJson() {
		const result: any[] = [];
		this.inOrderTraversalToJsonList(this.root, result)
		return result;
	}
}